import { useMemo } from 'react'
import { Aircraft, Event } from '../types/intellijetnet'
import { HiddenTransaction } from '../types/database'
import { HiddenTransactionsService } from '../services/hidden-transactions.service'
import { SoldAircraftItem } from './useMarketSoldData'
import { OffMarketAircraftItem } from './useMarketOffMarket'

interface UseMarketFilteringParams {
  historicalMonths: number
  recentlySoldAircraft: SoldAircraftItem[]
  removedFromMarketAircraft: { aircraft: Aircraft; event: Event | null }[]
  offMarketAircraftData: OffMarketAircraftItem[]
  aircraft: Aircraft[]
  hiddenTransactions: HiddenTransaction[]
}

export function useMarketFiltering({
  historicalMonths,
  recentlySoldAircraft,
  removedFromMarketAircraft,
  offMarketAircraftData,
  aircraft,
  hiddenTransactions,
}: UseMarketFilteringParams) {
  // Calculate cutoff date for historical data filtering in live view
  const historicalCutoffDateStr = useMemo(() => {
    const cutoffDate = new Date()
    cutoffDate.setMonth(cutoffDate.getMonth() - historicalMonths)
    return cutoffDate.toISOString().split('T')[0]
  }, [historicalMonths])

  // Filter sold aircraft based on historical months setting
  const filteredRecentlySoldAircraft = useMemo(() => {
    return recentlySoldAircraft.filter(item => {
      // Priority: aircraftTransaction.transaction_date > transaction.trans_date
      const dateSold = item.aircraftTransaction?.transaction_date || item.transaction?.trans_date
      if (!dateSold) return true // Include if no date available
      return dateSold >= historicalCutoffDateStr
    })
  }, [recentlySoldAircraft, historicalCutoffDateStr])

  // Build set of hidden transaction identifiers for filtering sold table
  const hiddenIdentifiers = useMemo(() => {
    return new Set(hiddenTransactions.map(h => h.transaction_identifier))
  }, [hiddenTransactions])

  // Filter out hidden transactions from the sold aircraft list
  const visibleRecentlySoldAircraft = useMemo(() => {
    if (hiddenIdentifiers.size === 0) return filteredRecentlySoldAircraft
    return filteredRecentlySoldAircraft.filter(item => {
      const id = HiddenTransactionsService.getTransactionIdentifier(
        item.aircraftTransaction,
        item.transaction
      )
      return !id || !hiddenIdentifiers.has(id)
    })
  }, [filteredRecentlySoldAircraft, hiddenIdentifiers])

  // Filter removed aircraft based on historical months setting
  const filteredRemovedFromMarketAircraft = useMemo(() => {
    return removedFromMarketAircraft.filter(item => {
      if (!item.event?.date) return true // Include if no date available
      return item.event.date >= historicalCutoffDateStr
    })
  }, [removedFromMarketAircraft, historicalCutoffDateStr])

  // Filter off-market sold/removed data based on historical months setting
  const filteredOffMarketSold = useMemo(() => {
    return offMarketAircraftData.filter(item => {
      if (item.offMarket.market_category !== 'recently_sold') return false
      const dateRef = item.offMarket.updated_at || item.offMarket.created_at
      if (!dateRef) return true // Include if no date available
      return dateRef >= historicalCutoffDateStr
    })
  }, [offMarketAircraftData, historicalCutoffDateStr])

  const filteredOffMarketRemoved = useMemo(() => {
    return offMarketAircraftData.filter(item => {
      if (item.offMarket.market_category !== 'removed_from_market') return false
      const dateRef = item.offMarket.updated_at || item.offMarket.created_at
      if (!dateRef) return true // Include if no date available
      return dateRef >= historicalCutoffDateStr
    })
  }, [offMarketAircraftData, historicalCutoffDateStr])

  // Filter off-market for-sale aircraft to exclude those that have been sold via a NEW API transaction
  // (i.e., the transaction occurred AFTER the off-market entry was created). Old/historical transactions
  // that predate the off-market entry are ignored — the user knowingly added the aircraft.
  // The auto-cleanup effect will also delete the off-market entry from the database for new transactions.
  const filteredOffMarketForSale = useMemo(() => {
    // Build a map of aircraft_id → most recent transaction date
    const soldAircraftTransactionDates = new Map<number, string>()
    for (const item of recentlySoldAircraft) {
      const transDate = item.aircraftTransaction?.transaction_date || item.transaction?.trans_date
      if (transDate) {
        soldAircraftTransactionDates.set(item.aircraft.aircraft_id, transDate)
      }
    }

    return offMarketAircraftData.filter(item => {
      if (item.offMarket.market_category !== 'for_sale') return false

      const transDate = soldAircraftTransactionDates.get(item.aircraft.aircraft_id)
      if (!transDate) return true // No transaction → keep in For Sale

      // Only exclude if the transaction occurred AFTER the off-market entry was created
      const offMarketCreatedAt = new Date(item.offMarket.created_at).getTime()
      const transactionDate = new Date(transDate).getTime()
      return transactionDate <= offMarketCreatedAt
    })
  }, [offMarketAircraftData, recentlySoldAircraft])

  // Create a memoized set of aircraft IDs that have been sold via API transactions
  // This is used to filter out sold aircraft from the For Sale container
  const soldAircraftIds = useMemo(() => {
    return new Set(recentlySoldAircraft.map(item => item.aircraft.aircraft_id))
  }, [recentlySoldAircraft])

  // Create a set of aircraft IDs that were sold RECENTLY (within last 30 days)
  // These should be excluded from For Sale even if forsale=true in API (stale data)
  // Aircraft sold more than 30 days ago can appear in For Sale if re-listed
  const recentlySoldAircraftIds = useMemo(() => {
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    const cutoffDateStr = thirtyDaysAgo.toISOString().split('T')[0]

    return new Set(
      recentlySoldAircraft
        .filter(item => {
          const dateSold = item.aircraftTransaction?.transaction_date || item.transaction?.trans_date
          // If no date, assume it's recent (be conservative)
          if (!dateSold) return true
          // Only include if sold within last 30 days
          return dateSold >= cutoffDateStr
        })
        .map(item => item.aircraft.aircraft_id)
    )
  }, [recentlySoldAircraft])

  // Filter API aircraft to exclude those that have been sold RECENTLY
  // Aircraft sold more than 30 days ago can appear in For Sale if they have forsale=true
  // This allows re-listed aircraft to show up in the For Sale container
  const filteredForSaleApiAircraft = useMemo(() => {
    return aircraft.filter(a => !recentlySoldAircraftIds.has(a.aircraft_id))
  }, [aircraft, recentlySoldAircraftIds])

  return {
    filteredRecentlySoldAircraft,
    hiddenIdentifiers,
    visibleRecentlySoldAircraft,
    filteredRemovedFromMarketAircraft,
    filteredOffMarketSold,
    filteredOffMarketRemoved,
    filteredOffMarketForSale,
    soldAircraftIds,
    filteredForSaleApiAircraft,
  }
}

